import React, { useState, useEffect } from 'react';
import './App.css';
import logo from './assets/crystal-LOGO.png';
import { getApiBaseUrl } from './config';
import { validatePhoneNumber } from './services/customerApi';
import { locationService, Country, Region, City, District } from './services/locationService';

// Kayıt linki zorunlu - direk kayıt kaldırıldı
const REQUIRE_REGISTRATION_LINK = true;

interface CustomerData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  gender: string;
  birthDate: string;
  education: string;
  profession: string;
  country: string;
  region: string;
  city: string;
  district: string;
  address: string;
  isTurkishCitizen: boolean;
  kvkkConsent: boolean;
  etkConsent?: boolean;
  membershipAgreement: boolean;
  marketingConsent?: boolean;
}

interface RegistrationLinkResponse {
  isValid: boolean;
  isExpired: boolean;
  isUsed: boolean;
  phoneNumber: string;
  firstName: string;
  lastName: string;
  createdAt: string;
  expiresAt: string;
  remainingMinutes: number;
  message: string;
  storeCode?: string;
  storeName?: string;
  storeCountry?: string;
  storeRegion?: string;
  storeCity?: string;
  storeDistrict?: string;
  storeAddress?: string;
}

interface RegisterWithLinkRequest {
  registrationId: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  gender: string;
  birthDate: string | null;
  education: string;
  profession: string;
  country: string;
  region: string;
  city: string;
  district: string;
  address: string;
  isTurkishCitizen: boolean;
  kvkkConsent: boolean;
  etkConsent: boolean;
  // Location kodları
  countryCode: string;
  stateCode: string;
  cityCode: string;
  districtCode: string;
}

function App() {
  const [formData, setFormData] = useState<CustomerData>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    gender: '',
    birthDate: '',
    education: '',
    profession: '',
    country: '',
    region: '',
    city: '',
    district: '',
    address: '',
    isTurkishCitizen: true,
    kvkkConsent: false,
    membershipAgreement: false,
    marketingConsent: false
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');

  // Location data states
  const [countries, setCountries] = useState<Country[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [regions, setRegions] = useState<Region[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<string>('');
  const [cities, setCities] = useState<City[]>([]);
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [districts, setDistricts] = useState<District[]>([]);
  const [selectedDistrict, setSelectedDistrict] = useState<string>('');

  // Helper: Load all cities for a country (across all regions)
  const loadAllCitiesForCountry = async (countryCode: string): Promise<City[]> => {
    if (!countryCode) return [];
    const regionsAll = await locationService.getRegions(countryCode);
    let allCities: City[] = [];
    for (const r of regionsAll) {
      const cs = await locationService.getCities(r.code);
      allCities = allCities.concat(cs);
    }
    // Unique by code
    const uniqueMap = new Map<string, City>();
    allCities.forEach(c => { if (!uniqueMap.has(c.code)) uniqueMap.set(c.code, c); });
    const uniqueCities = Array.from(uniqueMap.values()).sort((a,b) => a.name.localeCompare(b.name, 'tr'));
    return uniqueCities;
  };

  // Keep formData synced with selected city/district to ensure selects show values
  useEffect(() => {
    if (selectedCity && formData.city !== selectedCity) {
      setFormData(prev => ({ ...prev, city: selectedCity }));
    }
  }, [selectedCity, formData.city]);

  useEffect(() => {
    if (selectedDistrict && formData.district !== selectedDistrict) {
      setFormData(prev => ({ ...prev, district: selectedDistrict }));
    }
  }, [selectedDistrict, formData.district]);

  // Registration link states
  const [registrationId, setRegistrationId] = useState<string>('');
  const [isValidatingLink, setIsValidatingLink] = useState(false);
  const [linkError, setLinkError] = useState<string>('');
  
  // Cleanup timeouts on component unmount
  useEffect(() => {
    return () => {
      // Cleanup any pending timeouts
    };
  }, []);
  
  // Modal states for agreements
  const [isMembershipModalOpen, setIsMembershipModalOpen] = useState(false);
  const [isTicariIletiModalOpen, setIsTicariIletiModalOpen] = useState(false);
  const [isKvkkModalOpen, setIsKvkkModalOpen] = useState(false);


  // Validate registration link
  const validateRegistrationLink = async (id: string) => {
    setIsValidatingLink(true);
    setLinkError('');
    try {
      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}/api/RegistrationLink/${id}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }

      const validationData: RegistrationLinkResponse = await response.json();

      if (!validationData.isValid) {
        setLinkError('Bu kayıt linki geçersiz.');
      } else if (validationData.isExpired) {
        setLinkError('Bu kayıt linkinin süresi dolmuş.');
      } else if (validationData.isUsed) {
        setLinkError('Bu kayıt linki daha önce kullanılmış.');
            } else {
        // Link is valid, populate phone number, name and store address info
        // Normalize phone for UI and show with +90 prefix
        let uiPhone = (validationData.phoneNumber || '').replace(/\D/g, '');
        if (uiPhone.startsWith('90')) uiPhone = uiPhone.slice(2);
        if (uiPhone.startsWith('0')) uiPhone = uiPhone.slice(1);
        if (uiPhone.length > 10) uiPhone = uiPhone.slice(-10);
        const uiPhoneDisplay = uiPhone ? `+90${uiPhone}` : '';
        setFormData(prev => ({
          ...prev,
          phoneNumber: uiPhoneDisplay,
          firstName: validationData.firstName || '',
          lastName: validationData.lastName || ''
        }));

        // Store codes (trim paddings from API)
        const storeCountryCode = (validationData.storeCountry || '').trim();
        const storeRegionCode = (validationData.storeRegion || '').trim();
        const storeCityCode = (validationData.storeCity || '').trim();
        const storeDistrictCode = (validationData.storeDistrict || '').trim();

        // Prefill using codes -> resolve names from Country API
        try {
          // Countries
            const countriesData = await locationService.getCountries();
            setCountries(countriesData);
          const countryHit = countriesData.find(c => (c.code || '').trim() === storeCountryCode);
          const countryName = (countryHit?.name || '').trim() || storeCountryCode;
          setSelectedCountry(countryName);
          setFormData(prev => ({ ...prev, country: countryName }));

          // Regions
          const regionsData = await locationService.getRegions(storeCountryCode);
              setRegions(regionsData);
          const regionHit = regionsData.find(r => (r.code || '').trim() === storeRegionCode);
          const regionName = (regionHit?.name || '').trim() || storeRegionCode;
          setSelectedRegion(regionName);
          setFormData(prev => ({ ...prev, region: regionName }));

          // Cities (load across country to be robust)
          const citiesData = await loadAllCitiesForCountry(storeCountryCode);
                setCities(citiesData);
          const cityHit = citiesData.find(c => (c.code || '').trim() === storeCityCode);
          const cityName = (cityHit?.name || '').trim() || storeCityCode;
          setSelectedCity(cityName);
          setFormData(prev => ({ ...prev, city: cityName }));

          // Districts
          const dists = await locationService.getDistricts(storeCityCode);
                    setDistricts(dists);
          const distHit = dists.find(d => (d.code || '').trim() === storeDistrictCode);
          const districtName = (distHit?.name || '').trim() || storeDistrictCode;
          setSelectedDistrict(districtName);
          setFormData(prev => ({ ...prev, district: districtName }));
        } catch (prefillErr) {
          console.error('Otomatik lokasyon ön yükleme hatası (link):', prefillErr);
        }
      }
    } catch (error) {
      console.error('Link validation error:', error);
      setLinkError('Kayıt linki kontrol edilirken bir hata oluştu.');
    } finally {
      setIsValidatingLink(false);
    }
  };

  // Extract registration ID from URL and validate link
  useEffect(() => {
    if (!REQUIRE_REGISTRATION_LINK) return; // bypass link requirement

    const extractRegistrationId = () => {
      const path = window.location.pathname;
      const match = path.match(/\/register\/([a-f0-9]{8})/i);
      
      if (match && match[1]) {
        const id = match[1];
        setRegistrationId(id);
        validateRegistrationLink(id);
      } else {
        setLinkError('Lütfen Mağazamızla İletişime Geçiniz.');
      }
    };

    extractRegistrationId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  /** Doğum tarihi: frontta gg-aa-yyyy (DD-MM-YYYY) formatında tutulur, sadece rakam + tire. */
  const formatBirthDateInput = (raw: string): string => {
    const digits = raw.replace(/\D/g, '').slice(0, 8);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4, 8)}`;
  };

  /** gg-aa-yyyy → yyyy-aa-gg (API/SQL için). */
  const birthDateToApi = (ddMmYyyy: string): string | null => {
    const s = ddMmYyyy?.trim() || '';
    if (!s) return null;
    const m = s.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (!m) return null;
    return `${m[3]}-${m[2]}-${m[1]}`;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => {
      let newValue: string | boolean = type === 'checkbox' ? checked : value;
      if (name === 'birthDate' && typeof value === 'string') {
        newValue = formatBirthDateInput(value);
      }
      const newData = {
        ...prev,
        [name]: newValue
      };
      
      // Birleşik KVKK+ETK checkbox'ı işaretlenirse/çıkarılırsa
      if (name === 'kvkkEtkConsent') {
        newData.kvkkConsent = checked;
        newData.marketingConsent = checked;
        newData.etkConsent = checked;
      }
      
      return newData;
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    // Zorunlu alanlar: Ad, Soyad, Telefon
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Ad alanı boş bırakılamaz. Lütfen adınızı giriniz.';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Soyad alanı boş bırakılamaz. Lütfen soyadınızı giriniz.';
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Telefon numarası zorunludur. Lütfen cep telefonu numaranızı giriniz.';
    } else if (!validatePhoneNumber(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Geçerli bir Türkiye cep telefonu numarası giriniz (10 haneli, başında 0 olmadan, örn: 5424385769).';
    }

    // Üyelik sözleşmesi zorunlu
    if (!formData.membershipAgreement) {
      newErrors.membershipAgreement = 'Üyelik işleminin tamamlanması için üyelik sözleşmesini okuyup onaylamanız gerekmektedir.';
    }

    // KVKK onayı opsiyonel - sadece veri işleme mantığını kontrol eder

    // E-posta opsiyonel ama girilirse format kontrolü
    if (formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Girdiğiniz e-posta adresi geçerli formatta değil. Lütfen @ ve domain içeren geçerli bir e-posta giriniz (örn: ornek@email.com).';
    }

    // Doğum tarihi opsiyonel ama girilirse format ve aralık kontrolü
    const birthStr = formData.birthDate.trim();
    if (birthStr) {
      if (!/^\d{2}-\d{2}-\d{4}$/.test(birthStr)) {
        newErrors.birthDate = 'Doğum tarihi GG-AA-YYYY formatında olmalıdır (örn: 15-05-1990).';
      } else {
        const [dd, mm, yyyy] = birthStr.split('-').map(Number);
        const year = yyyy;
        const month = mm - 1; // JS ay 0-11
        const day = dd;
        const birthDate = new Date(year, month, day);
        const today = new Date();
        const minBirthYear = today.getFullYear() - 100; // En fazla 100 yaşında olanlar girebilsin
        // Geçerli tarih mi (ay/gün taşması kontrolü)
        if (birthDate.getFullYear() !== year || birthDate.getMonth() !== month || birthDate.getDate() !== day) {
          newErrors.birthDate = 'Girilen doğum tarihi geçerli bir gün değildir. Lütfen doğru gün ve ay giriniz.';
        } else if (year < minBirthYear) {
          newErrors.birthDate = `Doğum tarihi ${minBirthYear} yılından önce olamaz. Lütfen ${minBirthYear} veya daha sonrası için bir tarih giriniz (en fazla 100 yaş).`;
        } else if (birthDate > today) {
          newErrors.birthDate = 'Doğum tarihi gelecek bir tarih olamaz. Lütfen bugün veya öncesi bir tarih giriniz.';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      const baseUrl = getApiBaseUrl();

      if (REQUIRE_REGISTRATION_LINK) {
        // Seçilen location kodlarını çıkar
        const locationCodes = locationService.extractLocationCodes(
          selectedCountry,
          selectedRegion,
          selectedCity,
          selectedDistrict,
          countries,
          regions,
          cities,
          districts
        );

        // Form verilerini cdCurrAcc formatına dönüştür (link ile)
        console.log('=== FORM SUBMISSION START ===');
        console.log('1. Original Form Data:', {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          gender: formData.gender,
          birthDate: formData.birthDate,
          education: formData.education,
          profession: formData.profession,
          country: formData.country,
          region: formData.region,
          city: formData.city,
          district: formData.district,
          address: formData.address,
          isTurkishCitizen: formData.isTurkishCitizen,
          kvkkConsent: formData.kvkkConsent,
          etkConsent: formData.marketingConsent
        });
        
        // Telefon numarasını formatla: +90 prefix'ini kaldır, sadece rakamları al
        let phoneForApi = formData.phoneNumber?.trim() || '';
        const originalPhone = phoneForApi;
        if (phoneForApi) {
          // +90, 90, 0 prefix'lerini temizle
          phoneForApi = phoneForApi.replace(/\D/g, '');
          if (phoneForApi.startsWith('90')) phoneForApi = phoneForApi.substring(2);
          if (phoneForApi.startsWith('0')) phoneForApi = phoneForApi.substring(1);
          // +90 prefix'i ile gönder
          phoneForApi = phoneForApi ? `+90${phoneForApi}` : '';
        }
        console.log('2. Phone Number Formatting:', {
          original: originalPhone,
          formatted: phoneForApi
        });
        
        console.log('3. Location Codes:', {
          countryCode: locationCodes.countryCode,
          stateCode: locationCodes.stateCode,
          cityCode: locationCodes.cityCode,
          districtCode: locationCodes.districtCode,
          selectedCountry: selectedCountry,
          selectedRegion: selectedRegion,
          selectedCity: selectedCity,
          selectedDistrict: selectedDistrict
        });
        
      const apiData: RegisterWithLinkRequest = {
        registrationId: registrationId,
        firstName: formData.firstName?.trim() || '',
        lastName: formData.lastName?.trim() || '',
        email: formData.email?.trim() || '',
        phoneNumber: phoneForApi,
        gender: formData.gender || '',
        birthDate: birthDateToApi(formData.birthDate),
        education: formData.education || '',
        profession: formData.profession?.trim() || '',
        country: formData.country || '',
        region: formData.region || '',
        city: formData.city || '',
        district: formData.district || '',
        address: formData.address?.trim() || '',
        isTurkishCitizen: formData.isTurkishCitizen || false,
        kvkkConsent: formData.kvkkConsent || false,
        etkConsent: formData.marketingConsent || false,
        // Location kodlarını ekle
        countryCode: locationCodes.countryCode,
        stateCode: locationCodes.stateCode,
        cityCode: locationCodes.cityCode,
        districtCode: locationCodes.districtCode
      };
      
        console.log('4. Final API Request Data:', JSON.stringify(apiData, null, 2));
        console.log('5. API Endpoint:', `${baseUrl}/api/Customers/register-with-link`);
        console.log('=== FORM SUBMISSION END ===');
      const response = await fetch(`${baseUrl}/api/Customers/register-with-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(apiData),
      });

      console.log('6. API Response Status:', response.status, response.statusText);
      console.log('7. API Response Headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        let errorMessage = `HTTP Error: ${response.status}`;
        try {
          const errorData = await response.json();
          console.error('8. API Error Response:', JSON.stringify(errorData, null, 2));
          errorMessage = errorData.message || errorMessage;
        } catch (parseError) {
          console.error('8. Error parsing error response:', parseError);
          const errorText = await response.text();
          console.error('8. Raw error response:', errorText);
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('8. API Response Success:', JSON.stringify(result, null, 2));
        if (result.success) {
          setIsSubmitted(true);
          // Form reset işlemini kaldır - müşteri başarı sayfasında kalacak
        } else {
          console.error('9. API Response Failed:', result.message || 'Kayıt işlemi başarısız oldu.');
          setSubmitError(result.message || 'Kayıt işlemi başarısız oldu.');
        }
      } else {
        setSubmitError('Kayıt linki gerekli. Lütfen mağazamızla iletişime geçiniz.');
      }
    } catch (error) {
      console.error('10. Registration Error:', error);
      console.error('10. Error Details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        error: error
      });
      let errorMessage = error instanceof Error ? error.message : 'Beklenmeyen bir hata oluştu.';
      // Teknik hata mesajlarını kullanıcıya açıklayıcı Türkçe mesajlara çevir
      if (errorMessage.includes('SqlDateTime') || errorMessage.includes('DateTime overflow') || errorMessage.includes('1753') || errorMessage.includes('9999')) {
        const minYear = new Date().getFullYear() - 100;
        errorMessage = `Doğum tarihi sistemde kabul edilen aralıkta değil. Lütfen doğum tarihinizi ${minYear} ile bugün arasında, GG-AA-YYYY formatında (örn: 15-05-1990) tekrar giriniz.`;
      } else if (errorMessage.includes('phone') || errorMessage.includes('Phone') || errorMessage.includes('telefon')) {
        errorMessage = 'Girilen telefon numarası kullanılamıyor veya zaten kayıtlı olabilir. Lütfen farklı bir numara deneyin veya mağazamızla iletişime geçin.';
      } else if (errorMessage.includes('HTTP Error: 400')) {
        errorMessage = 'Gönderilen bilgilerde hata var. Lütfen tüm zorunlu alanları doğru doldurduğunuzdan ve doğum tarihinin geçerli aralıkta (en fazla 100 yaş) olduğundan emin olun.';
      } else if (errorMessage.includes('HTTP Error: 500') || errorMessage.includes('HTTP Error: 502')) {
        errorMessage = 'Kayıt sırasında sunucu hatası oluştu. Lütfen kısa süre sonra tekrar deneyin. Sorun devam ederse mağazamızla iletişime geçin.';
      }
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
      console.log('11. Form submission completed');
    }
  };

  // Show loading while validating link
  if (REQUIRE_REGISTRATION_LINK && isValidatingLink) {
    return (
      <div className="min-h-screen bg-gradient-to-br py-12 px-4 flex items-center justify-center">
        <div className="text-center">
          <img src={logo} alt="Gallery Crystal Logo" className="logo mx-auto mb-6" />
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Kayıt linki kontrol ediliyor...</p>
        </div>
      </div>
    );
  }

  // Show error if link is invalid
  if (REQUIRE_REGISTRATION_LINK && linkError) {
    return (
      <div className="min-h-screen bg-gradient-to-br py-12 px-4 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <img src={logo} alt="Gallery Crystal Logo" className="logo mx-auto mb-6" />
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="mb-4" style={{ color: '#B1A270' }}>
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold mb-2" style={{ color: '#B1A270' }}>Kayıt Linkinizin Süresi Dolmuştur</h2>
            <p className="mb-4" style={{ color: '#B1A270' }}>Lütfen Mağazamızla İletişime Geçiniz.</p>
          </div>
        </div>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="success-container">
        <div className="success-card">
          <div className="mb-6">
            <div className="success-icon-container">
              <svg className="success-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Hoşgeldin!</h2>
            <p className="text-gray-600">
              Crystal Family Ayrıcalıklarını Keşfetmeye Hazırsın.
            </p>
            <p className="text-gray-600 mt-2">
              Keyifli Alışverişler.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br py-12 px-4">
      <div className="form-container">
        <div className="form-header">
          <img src={logo} alt="Gallery Crystal Logo" className="logo" />
          <h1>Crystal Family</h1>
          <p>Evinde Değer Yaratanlar İçin</p>
        </div>

        <form onSubmit={handleSubmit} className="form-body">
          {/* Ad Soyad */}
          <div className="form-row two-cols">
            <div>
              <label htmlFor="firstName">Ad *</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className={errors.firstName ? 'error' : ''}
                placeholder="Adınızı giriniz"
              />
              {errors.firstName && (
                <p className="error-message">{errors.firstName}</p>
              )}
            </div>

            <div>
              <label htmlFor="lastName">Soyad *</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className={errors.lastName ? 'error' : ''}
                placeholder="Soyadınızı giriniz"
              />
              {errors.lastName && (
                <p className="error-message">{errors.lastName}</p>
              )}
            </div>
          </div>

          {/* E-posta ve Telefon */}
          <div className="form-row two-cols">
            <div>
              <label htmlFor="email">E-posta Adresi</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={errors.email ? 'error' : ''}
                placeholder="ornek@email.com (Opsiyonel)"
              />
              {errors.email && (
                <p className="error-message">{errors.email}</p>
              )}
            </div>

            <div>
              <label htmlFor="phoneNumber">Cep Telefonu *</label>
              <div className="phone-input-container">
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                className={errors.phoneNumber ? 'error' : ''}
                  placeholder="5XXXXXXXXX"
                  maxLength={14}
                  readOnly
                  style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
              />
              </div>
              {errors.phoneNumber && (
                <p className="error-message">{errors.phoneNumber}</p>
              )}
            </div>
          </div>

          {/* Cinsiyet ve Doğum Tarihi (Opsiyonel) */}
          <div className="form-row two-cols">
            <div>
              <label htmlFor="gender">Cinsiyet</label>
              <select
                id="gender"
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
                className={errors.gender ? 'error' : ''}
              >
                <option value="">Cinsiyet seçiniz (Opsiyonel)</option>
                <option value="erkek">Erkek</option>
                <option value="kadın">Kadın</option>
              </select>
              {errors.gender && (
                <p className="error-message">{errors.gender}</p>
              )}
            </div>

            <div>
              <label htmlFor="birthDate">Doğum Tarihi</label>
              <input
                type="text"
                id="birthDate"
                name="birthDate"
                value={formData.birthDate || ''}
                onChange={handleInputChange}
                onKeyDown={(e) => {
                  const key = e.key;
                  const allowed = /^\d$/.test(key) ||
                    key === 'Backspace' || key === 'Delete' || key === 'Tab' ||
                    key === 'ArrowLeft' || key === 'ArrowRight' || key === 'Home' || key === 'End' ||
                    (e.ctrlKey || e.metaKey) && (key === 'a' || key === 'c' || key === 'v' || key === 'x');
                  if (!allowed) e.preventDefault();
                }}
                className={errors.birthDate ? 'error' : ''}
                placeholder="gg-aa-yyyy (örn: 15-05-1990, en fazla 100 yaş)"
                inputMode="numeric"
                autoComplete="bday"
                maxLength={10}
              />
              {errors.birthDate && (
                <p className="error-message">{errors.birthDate}</p>
              )}
            </div>
          </div>

          {/* Eğitim Durumu ve Meslek (Opsiyonel) */}
          <div className="form-row">
            <div>
              <label htmlFor="profession">Mesleğiniz</label>
              <select
                id="profession"
                name="profession"
                value={formData.profession}
                onChange={handleInputChange}
                className={errors.profession ? 'error' : ''}
              >
                <option value="">Mesleğinizi seçiniz (Opsiyonel)</option>
                <option value="ev_hanimi">Ev Hanımı</option>
                <option value="ogrenci">Öğrenci</option>
                <option value="saglik_calisani">Sağlık Çalışanı</option>
                <option value="ogretmen_akademisyen">Öğretmen / Akademisyen</option>
                <option value="memur_kamu_calisani">Memur / Kamu Çalışanı</option>
                <option value="serbest_meslek_esnaf">Serbest Meslek / Esnaf</option>
                <option value="beyaz_yaka_calisan">Beyaz Yaka Çalışan</option>
                <option value="mavi_yaka_calisan">Mavi Yaka Çalışan</option>
                <option value="emekli">Emekli</option>
              </select>
              {errors.profession && (
                <p className="error-message">{errors.profession}</p>
              )}
            </div>
          </div>

          {/* Ülke/Bölge alanları müşteriye gösterilmez; arka planda link doğrulamasından otomatik set edilir. */}
          {/* İl ve İlçe bilgileri arka planda otomatik olarak set edilir */}

          {/* Üyelik Sözleşmesi ve Diğer Onaylar */}
          <div className="form-row">
            <div className="agreements-section">
              {/* 1. Üyelik Sözleşmesi */}
              <div className="agreement-item">
              <label className="checkbox-container">
                <input
                  type="checkbox"
                    name="membershipAgreement"
                    checked={formData.membershipAgreement}
                  onChange={handleInputChange}
                  className="checkbox-input"
                />
                  <span className="checkbox-text">
                    <span 
                      onClick={(e) => {
                        e.preventDefault();
                        setIsMembershipModalOpen(true);
                      }}
                      style={{ textDecoration: 'underline', cursor: 'pointer', color: '#0066cc' }}
                    >
                      Üyelik Sözleşmesini
                    </span>
                    {' '}okudum ve kabul ediyorum. *
                  </span>
              </label>
                {errors.membershipAgreement && (
                  <p className="error-message">{errors.membershipAgreement}</p>
                )}
          </div>

              {/* 2. Birleşik KVKK + ETK Checkbox */}
              <div className="agreement-item">
                <label className="checkbox-container" style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <input
                    type="checkbox"
                    name="kvkkEtkConsent"
                    checked={formData.kvkkConsent && formData.marketingConsent}
                    onChange={handleInputChange}
                    className="checkbox-input"
                    style={{ marginTop: '4px', flexShrink: 0 }}
                  />
                  <span className="checkbox-text" style={{ flex: 1, marginLeft: '8px' }}>
                    <span 
                      onClick={(e) => {
                        e.preventDefault();
                        setIsTicariIletiModalOpen(true);
                      }}
                      style={{ textDecoration: 'underline', cursor: 'pointer', color: '#0066cc' }}
                    >
                      Ticari Elektronik İleti İzin Metni'
                    </span>
                    {' '}ni okudum. Pazarlama amaçlı sms, e-posta ve arama iletilerini almayı kabul ediyorum.
                    <br />
                    <span style={{ fontSize: '0.9em', marginTop: '4px', display: 'block' }}>
                      KVKK kapsamında yapılan detaylı bilgilendirmeye{' '}
                      <span 
                        onClick={(e) => {
                          e.preventDefault();
                          setIsKvkkModalOpen(true);
                        }}
                        style={{ textDecoration: 'underline', cursor: 'pointer', color: '#0066cc' }}
                      >
                        buradan
                      </span>
                      {' '}ulaşabilirsiniz.
                    </span>
                  </span>
                </label>
              </div>
                </div>
              </div>


          {/* Error Message */}
          {submitError && (
            <div className="form-row">
              <div className="error-container">
                <p className="error-message">{submitError}</p>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="pt-6">
            <button 
              type="submit" 
              className={`btn-primary ${isSubmitting ? 'loading' : ''}`}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Kayıt Yapılıyor...' : 'Kaydı Tamamla'}
            </button>
          </div>

        </form>
      </div>

      {/* Üyelik Sözleşmesi Modal */}
      {isMembershipModalOpen && (
        <div className="modal-overlay" onClick={() => setIsMembershipModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>ÜYELİK SÖZLEŞMESİ</h2>
              <button className="modal-close" onClick={() => setIsMembershipModalOpen(false)}>×</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px', textAlign: 'left' }}>
              <div style={{ whiteSpace: 'pre-line', lineHeight: '1.6' }}>
                <h3>1. Giriş</h3>
                <p><strong>1.1.</strong> İşbu Üyelik Sözleşmesi ("Koşullar"); GALLERY CRYSTAL web sitesini (www.gallerycrystal.com.tr) ("GALLERY CRYSTAL Websitesi") kullanan kişiler ("Kullanıcı") ve ziyaret eden kişiler ("Ziyaretçi") ile Mahmutbey mah. 2414. Sk. No:5/32 Bağcılar/ İstanbul adresi merkezli bir Türk şirketi olan GALLERY CRYSTAL ("GALLERY CRYSTAL") arasında, Kullanıcı'nın GALLERY CRYSTAL web sitesi kullanımına ilişkin koşulları düzenleyen bağlayıcı bir sözleşme kurmaktadır.</p>
                <p><strong>1.2.</strong> Ziyaretçi; Website'ye erişmekle işbu Kullanım Koşulları'nın kendisine yönelik hükümlerine tabi olur.</p>
                <p><strong>1.3.</strong> Ziyaretçi, Website'ye erişmekle işbu Koşullar'ın kendisine yönelik hükümlerini okuduğunu, anladığını, kabul ettiğini ve kabul etmemesi halinde Website'ye erişimini derhal durduracağını beyan eder.</p>
                <p><strong>1.4.</strong> Kullanıcı; GALLERY CRYSTAL websitesini kullanmakla işbu Koşullar'a tabi olur.</p>
                <p><strong>1.5.</strong> Kullanıcı, GALLERY CRYSTAL websitesine erişim sağlayarak veya kullanarak işbu Koşullar'ı okuduğunu, anladığını, kabul ettiğini, işbu Koşullar ile bağlı olduğunu ve işbu Koşullar'a uyacağını beyan eder. Kullanıcı, Koşullar'da düzenlenen hükümlerin bir kısmını veya tamamını kabul etmemesi halinde GALLERY CRYSTAL websitesi kullanımını derhal sonlandırmalıdır.</p>
                <p><strong>1.6.</strong> İşbu Koşullar, GALLERY CRYSTAL Websitesine eklenen yeni işlevler, özellikler veya araçlar da dahil ancak bunlarla sınırlı olmamak üzere tüm eklemeler için geçerlidir.</p>
                <p><strong>1.7.</strong> GALLERY CRYSTAL, Kullanıcı'ya güncellemeler ve değişiklikler hakkında bildirimde bulunarak bu Koşullar'ı güncelleme ve değiştirme hakkını saklı tutar.</p>
                <p><strong>1.8.</strong> Herhangi bir güncelleme olması halinde, Kullanıcı'ya bekleyen güncellemeler önceden bildirilecek olup, söz konusu güncellemelerin uygulanmaya başlanmasından sonra, Kullanıcı'nın GALLERY CRYSTAL websitesini kullanmaya devam etmesi değişiklikleri kabul ettiği anlamına gelecektir.</p>
                <p><strong>1.9.</strong> Herhangi bir güncelleme halinde, Ziyaretçi önceden bilgilendirilmeyecek olup Ziyaretçi, işbu Koşullar'ın en güncel haline Website'sinden erişebilecektir.</p>

                <h3>2. Tanımlar</h3>
                <p><strong>2.1.</strong> İşbu terimler aksi belirtilmedikçe aşağıda ifade edildiği gibi anlaşılmalıdır:</p>
                <p><strong>Website:</strong> GALLERY CRYSTAL'e ait www.gallerycrystal.com.tr adlı websitesi.</p>
                <p><strong>Crystal Family:</strong> GALLERY CRYSTAL tarafından sunulan sadakat ve avantaj programı.</p>
                <p><strong>Puan:</strong> Crystal Family programı kapsamında alışverişlerden kazanılan ve belirli koşullar dahilinde kullanılabilecek dijital puan.</p>
                <p><strong>Ziyaretçi:</strong> www.gallerycrystal.com.tr websitesini ziyaret eden kişi.</p>
                <p><strong>Kullanıcı:</strong> www.gallerycrystal.com.tr Websitesine kayıt olarak kullanan gerçek kişi.</p>
                <p><strong>Fikri Mülkiyet Hakları:</strong> Bütün dünya ya da herhangi bir yerindeki telif hakkı, ticari sırlar, know-how, işletme adları, ticari markalar, hizmet markaları ve patentler gibi tescilli ya da tescilsiz, tescil edilebilir ya da tescil edilemez tüm fikri mülkiyet hakları</p>
                <p><strong>Güncelleme:</strong> GALLERY CRYSTAL Websitesinde periyodik veya tek seferlik yapılan güncelleme ya da basit geliştirme</p>
                <p><strong>Hesap:</strong> Kullanıcı'nın GALLERY CRYSTAL Websitesi ve özelliklerine erişmesine izin veren hesap</p>
                <p><strong>Mücbir Sebep:</strong> Sel, deprem ve benzeri doğal afetler; salgın ya da yaygın hastalıklar, herhangi bir ülkedeki savaş, toplumsal kargaşa veya devrimler ya da; kontrol dışı gerçekleşebilecek ve borçlunun kusurunu ortadan kaldıracak herhangi bir olay</p>

                <h3>3. Website</h3>
                <p><strong>3.1.</strong> GALLERY CRYSTAL, Website'ye erişim sağlanabilmesi için gereken uygun elektronik cihaz veya ekipmanları sağlamamaktadır. Website'ye erişmek için uygun cihazları temin etmek Ziyaretçi'nin sorumluluğundadır.</p>
                <p><strong>3.2.</strong> Website'ye erişim için gereken internet bağlantısı GALLERY CRYSTAL tarafından sunulmamakta olup, Ziyaretçi tarafından sağlanacaktır.</p>
                <p><strong>3.3.</strong> Ziyaretçi, Website'nin her zaman güncel olmayabileceğini kabul eder.</p>
                <p><strong>3.4.</strong> Ziyaretçi, Website'de yer alan kampanyaların değişmesi haline GALLERY CRYSTAL'ye karşı herhangi bir talepte bulunamaz.</p>
                <p><strong>3.5.</strong> Ziyaretçi, Website'den GALLERY CRYSTAL ile iletişime geçebilir ancak GALLERY CRYSTAL'nin anında yanıt verme yükümlülüğü bulunmamaktadır.</p>
                <p><strong>3.6.</strong> Website, GALLERY CRYSTAL'ye ait olmayan veya GALLERY CRYSTAL'nin kontrolü altında bulunmayan üçüncü taraf websitelerine, uygulamalarına veya hizmetlerine ilişkin bağlantılar sunabilir. Website'den ayrılan Ziyaretçi, üçüncü taraf websiteleri, uygulamaları ya da hizmetlerinin kullanıcı koşullarına tabii olacaktır.</p>

                <h3>4. Crystal Family Sadakat Programı Kampanya Koşulları</h3>
                <p><strong>4.1.</strong> Crystal Family, "Kampanya Koşullarına" bağlı kalmak kaydıyla kişiselleştirilmiş indirim seçenekleri ve puan kazanımı sunmaktadır.</p>
                <p><strong>4.2.</strong> Kullanıcı, hesap oluşturduğu anda, direkt olarak Crystal Family sadakat programından faydalanarak, kampanyaların süresi, yeri ve indirim oranı gibi kampanyaların sağladığı avantajlar konusunda detaylı bilgiye erişebilecektir.</p>
                <p><strong>4.3. Puan Kazanımı</strong></p>
                <p><strong>4.3.1.</strong> Crystal Family üyeleri yaptıkları alışverişlerden puan kazanabilirler.</p>
                <p><strong>4.3.2.</strong> Puan kazanımı yalnızca puan kullanılmayan alışverişlerde geçerlidir.</p>
                <p><strong>4.3.3.</strong> Puanlar, GALLERY CRYSTAL' in tercihi doğrultusunda kendi mağazalarında ve/veya online alışverişlerde kullanılabilir.</p>
                <p><strong>4.3.4.</strong> Puanlar, indirim kampanyalarıyla birlikte veya ayrı olarak uygulanabilir.</p>
                <p><strong>4.3.5.</strong> Puan kullanılarak yapılan alışverişlerde yeniden puan kazanılmaz.</p>
                <p><strong>4.3.6.</strong> Kampanyalara ek olarak tutarlı indirim, yüzdelik indirim veya ikinci alışverişe özel avantajlar dönemsel olarak sunulabilir.</p>
                <p><strong>4.4. Puanların Geçerlilik Süresi</strong></p>
                <p><strong>4.4.1.</strong> Kazanılan puanlar, kazanıldıkları takvim yılı sonuna kadar geçerlidir.</p>
                <p><strong>4.4.2.</strong> Kullanılmayan puanlar, yıl sonunda otomatik olarak sistemden silinir.</p>
                <p><strong>4.4.3.</strong> Puanlar devredilemez, nakde çevrilemez.</p>
                <p><strong>4.5. Puan İadesi</strong></p>
                <p><strong>4.5.1.</strong> Puan kullanılarak yapılan alışverişlerde faturanın tamamı iade edilirse, kullanılan puanlar hesabına geri yüklenir.</p>
                <p><strong>4.5.2.</strong> Ürün bazında (kısmi) iadelerde puan iadesi yapılmaz.</p>
                <p><strong>4.5.3.</strong> Puan kazanılan alışverişlerde ürün iade edilirse, kazanılan puanlar silinir.</p>

                <h3>5. GALLERY CRYSTAL Websitesi</h3>
                <p><strong>5.1.</strong> GALLERY CRYSTAL, GALLERY CRYSTAL Websitesi'ne erişim sağlanabilmesi için gereken uygun elektronik cihaz veya ekipmanları sağlamamaktadır. GALLERY CRYSTAL Websitesi'ni kullanmak için uygun cihazları temin etmek Kullanıcı'nın sorumluluğundadır.</p>
                <p><strong>5.2.</strong> GALLERY CRYSTAL Websitesi'ne herhangi bir platformdan girmek veya kullanmak için ücret talep edilmez. GALLERY CRYSTAL'in bu konuda değişiklik yapma hakkı saklıdır.</p>
                <p><strong>5.3.</strong> GALLERY CRYSTAL Websitesi, GALLERY CRYSTAL'e ait olmayan veya GALLERY CRYSTAL'in kontrolü altında bulunmayan üçüncü taraf websitelerine, uygulamalarına veya hizmetlerine ilişkin bağlantılar sunabilir. GALLERY CRYSTAL Websitesi'nden ayrılan Kullanıcı, üçüncü taraf websiteleri, uygulamaları ya da hizmetlerinin kullanıcı koşullarına tabii olacaktır.</p>

                <h3>6. Hesap</h3>
                <p><strong>6.1.</strong> İşbu Koşullar'ın 6.8 bölümünde bahsedilen GALLERY CRYSTAL Websitesi'nin sınırlı özelliklerine ve Gallery Crystal Sadakat Programı'na erişim sağlayabilmek için Kullanıcı, GALLERY CRYSTAL Websitesi'ne üye olmalı ve bir Hesap oluşturmalıdır.</p>
                <p><strong>6.2.</strong> Kullanıcı'nın GALLERY CRYSTAL Websitesi'ne erişimi ve GALLERY CRYSTAL Websitesi'ni kullanımı, bir Hesap oluşturmasına ve adı soyadı, e-posta adresi, şifre, telefon numarası, cinsiyet ve ülke dahil birtakım bilgileri sağlamasına bağlıdır. GALLERY CRYSTAL, üyelik ve Hesap oluşturma işlemleri için sağlanması gereken bilgilerde zaman zaman değişiklikler yapabilir.</p>
                <p><strong>6.3.</strong> Üyelik işlemlerinin tamamlanabilmesi için, talep edilen bilgilerde eksiklik olmaması gerekmekte olup, aksi halde GALLERY CRYSTAL, Kullanıcı'nın üyelik işlemlerine devam etmesine izin vermeyecektir.</p>
                <p><strong>6.4.</strong> Kullanıcı'nın GALLERY CRYSTAL Websitesinde bir Hesap oluşturabilmesi için en az 13 (onüç) yaşında olması gerekmektedir. Kullanıcı'nın 13 (onüç) yaşından büyük ancak 18 (onsekiz) yaşından küçük ("Küçük") olması halinde; Kullanıcı, ilgili Türk hukuku uyarınca ancak bir ebeveynin veya yasal vasinin onayıyla Hesap oluşturabilmektedir.</p>
                <p><strong>6.5.</strong> Kullanıcı, Hesap bilgilerini doğru, eksiksiz ve güncel tutmayı kabul eder. Kullanıcı, Hesabı kapsamında gerçekleştirilen tüm faaliyetlerden sorumludur.</p>
                <p><strong>6.6.</strong> Kullanıcı, Hesabına giriş yapabilmek için e-posta adresini ve GALLERY CRYSTAL Websitesi için oluşturduğu şifresini kullanacaktır.</p>
                <p><strong>6.7.</strong> Kullanıcı'nın şifresini unuttuğu durumlarda; "Şifrenizi mi unuttunuz?" butonuna basıldığında, Kullanıcı'ya şifresini sıfırlamasını sağlayan bir e-posta gönderilecektir.</p>
                <p><strong>6.8.</strong> Hesabına kayıt yapan Kullanıcı, aynı zamanda Crystal Family Sadakat Programı'na da dahil edilecek olup, sunulan avantaj ve kampanyalar hakkında bilgiye ulaşabilecek ve bu avantaj ve kampanyalardan "Kampanya Koşullarında" belirtildiği düzeyde faydalanabilecektir.</p>
                <p><strong>6.9.</strong> Kullanıcı, GALLERY CRYSTAL tarafından sağlanan bazı hizmetlerden yararlanabilmek için cihazında ilgili hizmetler için gerekli izinleri vermelidir.</p>

                <h3>7. Kullanıcı ve Ziyaretçi'nin Hak ve Yükümlülükleri</h3>
                <p><strong>7.1.</strong> Ziyaretçi, Websitesine kendisi tarafından sağlanan uyumlu bir elektronik cihaz ile erişmelidir.</p>
                <p><strong>7.2.</strong> Kullanıcı/Ziyaretçi; uluslararası, federal veya yerel kanunları, yönetmelikleri, hüküm ve düzenlemeleri ihlal etmemekle, GALLERY CRYSTAL'in Fikri Mülkiyet Hakları'nı ihlal edecek herhangi bir faaliyette bulunmamakla, yanlış ya da yanıltıcı bilgiler sağlamamakla yükümlüdür.</p>
                <p><strong>7.3.</strong> Kullanıcı, GALLERY CRYSTAL Websitesi'ne doğru bilgilerle kaydolmalıdır.</p>
                <p><strong>7.4.</strong> Crystal Family programı, kişiye özeldir. Kullanıcı hesap bilgilerini yalnızca kendisi ve çocukları için kullanabilir.</p>
                <p><strong>7.5.</strong> Kullanıcı, "Hesabımı Sil" butonuna tıklayarak GALLERY CRYSTAL Websitesinden çıkma talebinde bulunabilir.</p>

                <h3>8. GALLERY CRYSTAL'in Hak ve Yükümlülükleri</h3>
                <p><strong>8.1.</strong> GALLERY CRYSTAL, GALLERY CRYSTAL Websitesi'nde ve işbu Koşullar'da kendi inisiyatifi dâhilinde her zaman güncelleme yapma hakkına sahiptir.</p>
                <p><strong>8.2.</strong> GALLERY CRYSTAL, ödeme yöntemlerinde ve ödeme hizmeti sağlayıcılarında değişiklik yapma hakkını saklı tutar.</p>
                <p><strong>8.3.</strong> GALLERY CRYSTAL, Crystal Family Sadakat Programı, özelliklerini, tanımını ve kullanım sürelerini kanunların izin verdiği azami ölçüde değiştirebilir.</p>

                <h3>9. Sorumsuzluk</h3>
                <p><strong>9.1.</strong> GALLERY CRYSTAL' in herhangi bir üçüncü taraf websitesinin veya hizmetinin materyalleri, gizlilik politikaları veya prosedürleri üzerinde hiçbir etkisi yoktur ve GALLERY CRYSTAL, bunlar için herhangi bir sorumluluk kabul etmemektedir.</p>
                <p><strong>9.2.</strong> GALLERY CRYSTAL, Mağazalarında ve online platformlarında ödeme sisteminin hataları nedeniyle tahsil edilen hatalı ücretlendirmelerden sorumlu değildir.</p>
                <p><strong>9.3.</strong> GALLERY CRYSTAL, ödeme işlemleriyle ilişkili kişisel verilerden sorumlu tutulamaz.</p>

                <h3>10. Çeşitli Hükümler</h3>
                <p><strong>10.1.</strong> GALLERY CRYSTAL, Kullanıcı'nın/Ziyaretçi'nin kişisel verilerini 6698 sayılı Kişisel Verilerin Korunması Kanunu ("KVKK")'ndan kaynaklanan yükümlülüklerini yerine getirerek işler.</p>
                <p><strong>10.2.</strong> Bu Koşullar'ın herhangi bir bölümünün hukuka aykırı, hükümsüz veya uygulanamaz olduğu tespit edilirse, bu hukuka aykırılık, hükümsüzlük veya uygulanamazlık yalnızca ilgili madde kapsamında söz konusu olacaktır.</p>

                <h3>11. Bildirimler</h3>
                <p><strong>11.1.</strong> GALLERY CRYSTAL Websitesi, Website ve işbu Koşullar hakkında herhangi bir geri bildirim, soru veya yorum için kvkk@gallerycrystal.com.tr adresine e-posta göndererek veya Website üzerinden GALLERY CRYSTAL ile iletişime geçebilirsiniz.</p>

                <h3>12. Sözleşmenin Feshi</h3>
                <p>İşbu sözleşme herhangi bir sebeple şirket ya da kullanıcı tarafından üyeliğin iptal edilmesine kadar yürürlüktedir. Kullanıcı işbu sözleşmenin her hangi bir maddesini ihlal etmesi halinde şirket kullanıcının üyeliğini iptal etme ve sözleşmeyi feshetme hakkına her zaman sahiptir.</p>

                <h3>13. Uyuşmazlıkların Çözümü</h3>
                <p>İşbu sözleşmenin uygulanmasından kaynaklanan hukuki ihtilafların çözümünde Bakırköy Mahkemeleri ve İcra Daireleri yetkilidir.</p>

                <h3>14. Tebligat</h3>
                <p>İşbu sözleşmede tarafların belirtilen adresleri kendilerine ait resmi tebligat adresleri olup bu adreslere yapılacak tebligatlar geçerli olarak kabul edilecektir.</p>

                <h3>15. Yürürlük</h3>
                <p>İşbu sözleşme kullanıcının üyelik kaydı yaparak işbu sözleşmedeki tüm maddeleri okuduğu ve üyelik sözleşmesinde yer alan maddeleri kabul ettiği anlamına gelmektedir. İşbu sözleşme bu kapsamda kullanıcının üye olduğu anda akdedilmiş ve yürürlüğe girmiştir.</p>

                <p><strong>PSL İÇ VE DIŞ TİCARET AŞ.</strong></p>
                <p>Adres: Mahmutbey mah. 2414. Sk. No: 5/32 Bağcılar/İstanbul</p>
                <p>Telefon: 444 10 10</p>
                <p>Mersis No: 0733046960400001</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ticari İleti Onay Metni Modal */}
      {isTicariIletiModalOpen && (
        <div className="modal-overlay" onClick={() => setIsTicariIletiModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Ticari İleti Onay Metni</h2>
              <button className="modal-close" onClick={() => setIsTicariIletiModalOpen(false)}>×</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px', textAlign: 'left' }}>
              <div style={{ whiteSpace: 'pre-line', lineHeight: '1.6' }}>
                <p>Tarafıma, ürün ve hizmetleriniz, kampanyalar, indirimler, tanıtımlar, müşteri memnuniyeti anketleri, özel teklifler ve benzeri ticari elektronik iletiler hakkında bilgi verilmesi amacıyla e-posta, SMS, telefon araması ve diğer elektronik iletişim kanalları üzerinden ulaşılmasına onay veriyorum.</p>
                <p>Bu onay, 6563 sayılı Elektronik Ticaretin Düzenlenmesi Hakkında Kanun ve ilgili yönetmelik uyarınca alınmakta olup, vermiş olduğum rızayı dilediğim zaman İleti Yönetim Sistemi (İYS) üzerinden veya tarafınıza ileteceğim bir bildirimle geri çekme hakkına sahip olduğumu biliyorum.</p>
                <p>Tarafınıza ticari elektronik ileti gönderebilmek için kimlik, iletişim ve ticari elektronik ileti gönderimi tercihinize ilişkin verilerinizi işlemekteyiz. Bu veriler, ticari ileti onayı süresince veya onay geri çekilene kadar saklanmaktadır.</p>
                <p>Kişisel verileriniz veri güvenliğini sağlamak için gerekli idari ve teknik tedbirler alınarak korunmaktadır. Kişisel verilerinizin işlenmesine ilişkin detaylı bilgi için <a href="https://www.gallerycrystal.com.tr/kvkk-aydinlatma-metni" target="_blank" rel="noopener noreferrer">https://www.gallerycrystal.com.tr/kvkk-aydinlatma-metni</a> linkine bulunan Aydınlatma Metnimizi inceleyebilirsiniz.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* KVKK Aydınlatma Metni Modal */}
      {isKvkkModalOpen && (
        <div className="modal-overlay" onClick={() => setIsKvkkModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>KİŞİSEL VERİLERİN KORUNMASINA İLİŞKİN AYDINLATMA METNİ</h2>
              <button className="modal-close" onClick={() => setIsKvkkModalOpen(false)}>×</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '80vh', overflowY: 'auto', padding: '20px', textAlign: 'left' }}>
              <div style={{ lineHeight: '1.6' }}>
                <p>6698 Sayılı Kişisel Verilerin Korunması Hakkında Kanun'un ("KVKK") 10'uncu maddesi uyarınca GALLERY CRYSTAL ("Biz", "Veri Sorumlusu") olarak sizlerin https://www.gallerycrystal.com.tr internet sitemiz ("Websitesi", "Websayfası") üzerinden gerçekleştirilen Hesap ve eş zamanlı olarak Crystal Family Sadakat Programı üyeliği'ne ("Üyelik", "Üye") kayıt olabilmeniz için kimliğimiz, kişisel verilerinizi işleme amaçlarımız, kişisel verileri kime ve neden aktardığımız, hukuki sebeplerimiz ve haklarınız konusunda bilgilendirmek isteriz:</p>
                
                <p><strong>Veri Sorumlusu:</strong> PSL İÇ VE DIŞ TİC. A.Ş.</p>
                
                <p>Üye olmanız vasıtasıyla elde edilen kişisel verileriniz;</p>
                <ul>
                  <li>hukuka ve dürüstlük kurallarına uygun ve belirli, açık ve meşru amaçlar için işlenmekte,</li>
                  <li>doğru ve gerektikçe güncel tutulmakta,</li>
                  <li>işlendikleri amaçla bağlantılı, sınırlı ve ölçülü kullanılmakta ve,</li>
                  <li>hukuka uygun olarak belirlediğimiz saklama süresi boyunca muhafaza edilmektedir.</li>
                </ul>
                
                <table>
                  <thead>
                    <tr>
                      <th>Veri Kategorisi</th>
                      <th>Veri Türü</th>
                      <th>İlgili Kişi</th>
                      <th>İşleme Amacı</th>
                      <th>İşlememizdeki Hukuki Sebep</th>
                      <th>Toplama Yöntemi</th>
                      <th>Veri Aktarımı</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td rowSpan={3}><strong>Kimlik</strong></td>
                      <td>Ad Soyad</td>
                      <td>Üye olmak isteyen Websitesi ziyaretçileri</td>
                      <td>Sözleşmenin kurulacağı kişinin belirlenebilmesi</td>
                      <td>Üye ile kurulan hizmet sözleşmesinin kurulması ve ifası için ad soyadın gerekli olması (KVKK m.5/2-c, TBK, TTK ve VUK)</td>
                      <td>Üyelik formunun doldurulması ile, Otomatik</td>
                      <td>Kişisel verileriniz, yalnızca hizmetin sağlanması amacıyla kargo firmalarına, bilişim altyapı sağlayıcılarına ve gerekli durumlarda yasal mercilere aktarılabilmektedir.</td>
                    </tr>
                    <tr>
                      <td>Doğum Tarihi</td>
                      <td>Üye olmak isteyen Websitesi ziyaretçileri</td>
                      <td>Üye'nin yaşının doğrulanabilmesi ve doğum günlerinde ona özel ayrıcalıklar sunulabilmesi</td>
                      <td>Bir ayrıcalık hizmeti sunduğumuz bu programda bu tür verileri bilmemizin meşru menfaat (müşteri memnuniyetinin artırılması)</td>
                      <td>Üyelik formunun doldurulması ile, Otomatik</td>
                      <td>Kişisel verileriniz, yalnızca hizmetin sağlanması amacıyla kargo firmalarına, bilişim altyapı sağlayıcılarına ve gerekli durumlarda yasal mercilere aktarılabilmektedir.</td>
                    </tr>
                    <tr>
                      <td>Cinsiyet</td>
                      <td>Üye olmak isteyen Websitesi ziyaretçileri</td>
                      <td>Anonimleştirmek suretiyle üyeliğin cinsiyete göre dağılımının analiz edilebilmesi amacıyla</td>
                      <td>Meşru Menfaat</td>
                      <td>Üyelik formunun doldurulması ile, Otomatik</td>
                      <td>Veri aktarımı gerçekleştirilmemektedir.</td>
                    </tr>
                    <tr>
                      <td rowSpan={3}><strong>İletişim</strong></td>
                      <td>Adres Bilgisi</td>
                      <td>Üye olmak isteyen Websitesi ziyaretçileri</td>
                      <td>Üye ile iletişim sağlanabilmesi, gönderim sağlanabilmesi ve Crystal Family Sadakat Programından Faydalanabililinmesi</td>
                      <td>Meşru Menfaat (müşteri memnuniyetinin artırılması, hizmet kalitesinin ölçülmesi)</td>
                      <td>Üyelik formunun doldurulması ile, Otomatik</td>
                      <td>Kişisel verileriniz, yalnızca hizmetin sağlanması amacıyla kargo firmalarına, bilişim altyapı sağlayıcılarına ve gerekli durumlarda yasal mercilere aktarılabilmektedir.</td>
                    </tr>
                    <tr>
                      <td>Telefon Bilgisi</td>
                      <td>Üye olmak isteyen Websitesi ziyaretçileri</td>
                      <td>Üye ile iletişim sağlanabilmesi, gönderim sağlanabilmesi ve Crystal Family Sadakat Programından Faydalanabililinmesi</td>
                      <td>Meşru Menfaat (müşteri memnuniyetinin artırılması, hizmet kalitesinin ölçülmesi)</td>
                      <td>Üyelik formunun doldurulması ile, Otomatik</td>
                      <td>Kişisel verileriniz, yalnızca hizmetin sağlanması amacıyla kargo firmalarına, bilişim altyapı sağlayıcılarına ve gerekli durumlarda yasal mercilere aktarılabilmektedir.</td>
                    </tr>
                    <tr>
                      <td>E-posta Bilgisi</td>
                      <td>Üye olmak isteyen Websitesi ziyaretçileri</td>
                      <td>Üye ile iletişim sağlanabilmesi, gönderim sağlanabilmesi ve Crystal Family Sadakat Programından Faydalanabililinmesi</td>
                      <td>Meşru Menfaat (müşteri memnuniyetinin artırılması, hizmet kalitesinin ölçülmesi)</td>
                      <td>Üyelik formunun doldurulması ile, Otomatik</td>
                      <td>Kişisel verileriniz, yalnızca hizmetin sağlanması amacıyla kargo firmalarına, bilişim altyapı sağlayıcılarına ve gerekli durumlarda yasal mercilere aktarılabilmektedir.</td>
                    </tr>
                  </tbody>
                </table>

                <p style={{ marginTop: '1.5rem' }}><strong>Kişisel Verileriniz:</strong> Üyelik devam ettiği sürece ve sonrasında doğabilecek kanuni uyuşmazlıklara karşı 10 yıl süreyle saklanır; bu sürenin sonunda anonim hale getirilir veya imha edilir.</p>

                <h3>Sahip olduğunuz haklar aşağıdaki gibi özetlenebilir:</h3>
                <p>Websitesi üyelik hizmetlerine ilişkin kişisel verilerinizin; işlenip işlenmediğine, işlenme amacına, yurtiçine ya da yurtdışına aktarılmasına dair bilgi edinme; amacına uygun işlenmesini, yanlış veya eksikse düzeltilmesini ve üçüncü kişilerin haberdar edilmesini, ilgili sebepler ortadan kalktıysa imha edilmesini ve üçüncü kişilerin haberdar edilmesini ve, uğradığınız zararların tazmin edilmesini, talep etme; otomatik sistemlerce yapılan analizler sebebiyle aleyhinize çıkan sonuçlara itiraz etme.</p>

                <h3>Haklarınızı Kullanma Yöntemi</h3>
                <p>Yukarıda belirtilmiş olan hakları kullanımınıza dair beyanı, kimliğinizi tespit edici belgeler ve talep konusu ile beraber aşağıdaki kanallar vasıtasıyla tarafımıza iletebilirsiniz:</p>
                <ul>
                  <li>kvkk@gallerycrystal.com.tr adresine e-posta yolu ile güvenli elektronik imzalı ya da imzasız olarak iletmek suretiyle,</li>
                  <li>pslicvedisticaretas@hs03.kep.tr kayıtlı elektronik posta (KEP) adresine Kep yoluyla iletilmek suretiyle,</li>
                  <li>"Mahmutbey mah. 2414. Sk. No :5/32 Bağcılar/İstanbul" adresine elden teslim etmek veya Noter ya da iadeli taahhütlü posta göndermek suretiyle.</li>
                </ul>

                <p>Talebinizi 30 (otuz) günden fazla olmamak üzere en kısa sürede ve işlemin maliyeti olmadığı sürece ücret talep etmeksizin cevaplayacağımızı taahhüt ediyoruz.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;